import React from 'react';
import { Link } from 'react-router-dom';
import { Info, Repeat } from 'lucide-react';
import ContainerPageLayout, { CodeBlock, ReqBadge, DownloadButtons } from '../components/ContainerPageLayout';
import ApiPlayground from '../components/ApiPlayground';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'process', label: 'Recurring Billing Process' },
  { id: 'generate-rbid', label: 'Generate RBID', children: [
    { id: 'gen-fields', label: 'RB Tag Fields' },
    { id: 'gen-sample', label: 'Sample XML' },
    { id: 'gen-response', label: 'Response' },
  ]},
  { id: 'toggle-rbid', label: 'Activate / Deactivate RBID', children: [
    { id: 'drb-request', label: 'Making The Request' },
    { id: 'drb-fields', label: 'Form Fields' },
    { id: 'drb-request-reference', label: 'Request Reference' },
  ]},
  { id: 'process-rbid', label: 'Process RBID', children: [
    { id: 'prcb-request', label: 'Making The Request' },
    { id: 'prcb-fields', label: 'Form Fields' },
    { id: 'prcb-request-reference', label: 'Request Reference' },
  ]},
];

/* ============ Sample code ============ */
const GEN_SAMPLE_XML = `<!-- Generating RBID by supplying RB fields in an API Request Demonstration -->
<R>
    <R1>...</R1>
    ...
    <RB>
       <T>M</T>
    </RB>
</R>`;

const GEN_RESPONSE_XML = `<!-- Sample XML Document Including The Generated RBID -->
<?xml version="1.0"?>
<R>
    <R1>...</R1>
    ...
    <RBID>1342</RBID>
</R>`;

const DRB_REQUEST_URL = `https://staging.ecomm365.com/acqapi/service.ashx?Username=SomeName&Password=SomePassword&MessageID=30dd879c-ee2f-11db-8314-0800200c9a66&APISignature=deactivaterecurringbilling&Data=<R>...</R>`;

const DRB_XML = `<!-- Sample of RBID Activation -->
<R>
    <R1>1342</R1>
    <R2>1</R2>
</R>`;

const DRB_RESP_ACTIVATE = `<?xml version="1.0"?>
<R>
    <R1>0043</R1>
    <R2>Activate RecurringBilling Successful</R2>
</R>`;

const DRB_RESP_DEACTIVATE = `<?xml version="1.0"?>
<R>
    <R1>0043</R1>
    <R2>Deactivate RecurringBilling Successful</R2>
</R>`;

const PRCB_REQUEST_URL = `https://staging.ecomm365.com/acqapi/service.ashx?Username=SomeName&Password=SomePassword&MessageID=30dd879c-ee2f-11db-8314-0800200c9a66&APISignature=processrecurringbilling&Data=<R>...</R>`;

const PRCB_XML = `<!-- Process Recurring Billing Request Demonstration -->
<R>
    <R1>1342</R1>
    <R2>1000</R2>
    <udf>
        <I>eCOMM</I>
        <I>Subscription</I>
    </udf>
</R>`;

const PRCB_RESP_0051 = `<?xml version="1.0"?>
<R>
    <R1>0051</R1>
    <R2>Process Recurring Billing Received</R2>
</R>`;

const PRCB_RESP_RESULT = `<!-- Notification to the merchant's Notification URL, if setup -->
<?xml version="1.0"?>
<R>
    <R1>0044</R1>
    <R2>Successful</R2>
    <R3>1342</R3>
    <R4>Successful</R4>
    <R5>43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R5>
    <R6>RB_87b1ecbb-1ef2-4969-98f5-d68d81e7c0d3</R6>
    <udf>
        <I>eCOMM</I>
        <I>Subscription</I>
    </udf>
</R>`;

/* ============ Table data ============ */
const GEN_FIELDS = [
  { name: 'T', desc: 'Recurring Type. Manual = [M].', req: 'Y', def: 'A(1)' },
  { name: 'A', desc: 'Action Type. Pre-Auth = [S]. Only required when creating an RBID which is going to be applied on a Pre-Auth transaction. When the Pre-Auth is complete, an RBID is created.', req: 'N', def: 'A(1)' },
];

const DRB_FIELDS = [
  { name: 'R1', desc: 'Recurring Billing ID (RBID).', req: 'Y', def: 'AN(50)' },
  { name: 'R2', desc: 'RBID Status: On/Off. OFF = [0]. ON = [1].', req: 'Y', def: 'N(1)' },
];

const DRB_VALIDATION = [
  { name: 'R1', desc: 'Recurring Billing ID (RBID).', regex: '^[0-9]*$' },
  { name: 'R2', desc: 'RBID Status.', regex: '^(1|0)$' },
];

const PRCB_FIELDS = [
  { name: 'R1', desc: 'Recurring Billing ID (RBID).', req: 'Y', def: 'AN(50)' },
  { name: 'R2', desc: 'Total Transaction Amount (without decimals). E.g. €100.00 = [10000], €0.99 = [99].', req: 'Y', def: 'N(20)' },
  { name: 'udf', desc: 'UDF List (see API Overview).', req: 'N', def: 'AN(50)' },
];

const PRCB_VALIDATION = [
  { name: 'R1', desc: 'Recurring Billing ID (RBID).', regex: '^[0-9]*$' },
  { name: 'R2', desc: 'Total Transaction Amount (without decimals).', regex: '^[0-9]{0,20}$' },
];

const PRCB_RESULT_FIELDS = [
  { name: 'R1', desc: (<>Error Code (see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link>).</>), def: 'N(4)' },
  { name: 'R2', desc: 'Description — human-readable message paired with R1.', def: 'AN(250)' },
  { name: 'R3', desc: 'Recurring Billing ID (RBID).', def: 'N(20)' },
  { name: 'R4', desc: 'The transaction status.', def: 'AN(20)' },
  { name: 'R5', desc: 'Merchant Transaction Code.', def: 'AN(50)' },
  { name: 'R6', desc: 'Recurring Billing Generated Transaction Code.', def: 'AN(50)' },
  { name: 'R13', desc: 'Recurring Billing Error code (optional).', def: 'AN(50)' },
  { name: 'udf', desc: 'UDF List.', def: 'AN(50)' },
];

const RecurringBilling = () => (
  <ContainerPageLayout
    currentId="recurring-billing"
    title="Recurring Billing"
    subtitle="Process transactions without re-collecting card details — create a reusable Recurring Billing ID (RBID) once, then charge or subscribe the cardholder anytime."
    signature={["deactivaterecurringbilling", "processrecurringbilling"]}
    toc={TOC}
    mainTestId="recurring-billing-page"
    sidebarTestId="recurring-billing-sidebar"
  >
    {/* Overview */}
    <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Overview</h2>
      <div className="space-y-4 text-gray-700 text-base leading-relaxed">
        <p>
          The <strong>Recurring Billing</strong> API allows the merchant to process transactions without the need of the consumer's card information. The merchant must first register the consumer's card details into eCOMM's system where the response will contain an <strong>RBID (Recurring Billing ID)</strong>. The RBID is then used to process a transaction using the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">processrecurringbilling</code> API. The RBID is required when using both the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">DeactivateRecurringBilling</code> &amp; <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">ProcessRecurringBilling</code> APIs.
        </p>
        <p>
          To generate the RBID, the merchant must supply additional fields on the existing <Link to="/technical-documentation/api-reference/sale-pre-auth" className="text-[#1F4E79] underline hover:text-[#163a5f]">Sale API</Link> or the Hosted Payments Page API request. The RBID will then store the card holder's information so there is no need for card data on future transactions.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-5 flex items-start gap-3">
        <Info size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-700 space-y-2">
          <p>
            <strong>Zero-amount RBID creation trick:</strong> The API requires a real transaction to generate an RBID. If you want an RBID without charging the cardholder anything meaningful:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Quick:</strong> Sale of €0.01 (R3 = <code className="bg-white px-1 rounded">1</code>) → RBID created → call Refund.</li>
            <li><strong>Longer:</strong> Pre-Auth of €0.01 → Complete Pre-Auth (RBID generated at completion) → Refund.</li>
          </ul>
          <p className="italic">A value of <code className="bg-white px-1 rounded">0</code> is not accepted.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
        {[
          { t: 'Generate RBID', d: 'Add <RB> block to a Sale or HPP request. RBID returned in the response.', n: 1 },
          { t: 'Activate / Deactivate', d: 'Toggle RBID On/Off at any time using DeactivateRecurringBilling.', n: 2 },
          { t: 'Process RBID', d: 'Charge the stored card using ProcessRecurringBilling and the RBID.', n: 3 },
        ].map((c) => (
          <div key={c.n} className="rounded-lg border border-gray-200 bg-gradient-to-br from-white to-blue-50/40 p-4">
            <div className="inline-flex items-center justify-center w-7 h-7 bg-[#1F4E79] text-white rounded-full font-bold text-sm mb-2">{c.n}</div>
            <div className="text-sm font-bold text-gray-900 mb-1">{c.t}</div>
            <div className="text-xs text-gray-600 leading-snug">{c.d}</div>
          </div>
        ))}
      </div>
    </section>

    {/* Process */}
    <section id="process" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Recurring Billing Process</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Recurring Billing works with the <Link to="/technical-documentation/api-reference/sale-pre-auth" className="text-[#1F4E79] underline hover:text-[#163a5f]">Sale API</Link> or the Hosted Payments Page API to generate the RBID. Please ensure you've read those pages first to learn what information is required before proceeding.
      </p>
      <div className="space-y-5">
        <div>
          <h3 className="text-lg font-bold text-[#1F4E79] mb-1">Generate RBID</h3>
          <p className="text-sm text-gray-700">
            The merchant makes the initial API request while supplying the additional <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">&lt;RB&gt;</code> tags inside the XML document. Once successful, the response will contain a unique RBID in a field called <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">RBID</code>.
            If generating an RBID using a Pre-Auth, the RBID will only be generated once the Pre-Auth is <em>complete</em> — it will then be included in the confirmation notification sent from the Complete Pre-Auth request.
          </p>
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#1F4E79] mb-1">Deactivate / Activate RBID</h3>
          <p className="text-sm text-gray-700">
            Call the <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">DeactivateRecurringBilling</code> API with the RBID to flip its state. A response of <strong className="text-[#c32222]">"0043 — Activate RecurringBilling Successful"</strong> or <strong className="text-[#c32222]">"0043 — Deactivate RecurringBilling Successful"</strong> confirms the change.
          </p>
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#1F4E79] mb-1">Process RBID</h3>
          <p className="text-sm text-gray-700">
            Call the <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">ProcessRecurringBilling</code> API supplying the RBID. You'll receive <strong className="text-[#c32222]">"0051 — Process Recurring Billing Received"</strong> if the notification method is <em>Notification</em>, otherwise <strong className="text-[#c32222]">"0000 — Processed Successfully"</strong>. The transaction result notification is sent to your Notification URL.
          </p>
        </div>
      </div>
    </section>

    {/* Generate RBID */}
    <section id="generate-rbid" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Repeat size={22} className="text-[#1F4E79]" />
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Generate RBID</h2>
      </div>
      <p className="text-gray-700 text-base leading-relaxed mb-6">
        To generate the RBID on a transaction, the following data needs to be supplied with the Sale or Hosted Payments Page API call. This data must be supplied at the bottom of the XML Document inside the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;RB&gt;</code> parent tag.
      </p>

      {/* Fields */}
      <div id="gen-fields" className="scroll-mt-28 mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-3">RB Tag Fields</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#1F4E79] text-white">
                <th className="text-left p-3 w-20">Field</th>
                <th className="text-left p-3">Description</th>
                <th className="text-left p-3 w-24">Required</th>
                <th className="text-left p-3 w-28">Definition</th>
              </tr>
            </thead>
            <tbody>
              {GEN_FIELDS.map((f, i) => (
                <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                  <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                  <td className="p-3">{f.desc}</td>
                  <td className="p-3"><ReqBadge value={f.req} /></td>
                  <td className="p-3 font-mono text-xs">{f.def}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sample XML */}
      <div id="gen-sample" className="scroll-mt-28">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Sample Request XML</h3>
        <CodeBlock code={GEN_SAMPLE_XML} lang="xml" testid="gen-sample-xml" />
      </div>

      {/* Response */}
      <div id="gen-response" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Response — Including Generated RBID</h3>
        <p className="text-sm text-gray-600 mb-2">
          The RBID can be returned in the server response or sent to your Notification URL depending on your notification method.
        </p>
        <CodeBlock code={GEN_RESPONSE_XML} lang="xml" testid="gen-response-xml" />
      </div>
    </section>

    {/* Activate / Deactivate */}
    <section id="toggle-rbid" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Activate / Deactivate RBID</h2>

      <div id="drb-request" className="scroll-mt-28">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Making The Request</h3>
        <p className="text-sm text-gray-700 mb-3">The request string for the <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono">DeactivateRecurringBilling</code> call:</p>
        <ol className="list-decimal pl-6 space-y-1 text-gray-700 text-sm mb-4">
          <li><strong>Username</strong> = SomeName</li>
          <li><strong>Password</strong> = SomePassword</li>
          <li><strong>MessageID</strong> = <em>*GUID</em></li>
          <li><strong>APISignature</strong> = DeactivateRecurringBilling</li>
          <li><strong>Data</strong> = Form data in XML format</li>
        </ol>
        <CodeBlock code={DRB_REQUEST_URL} lang="http" testid="drb-request-url" />

        <div className="flex flex-wrap items-center justify-between gap-3 mb-2 mt-6">
          <h4 className="text-base font-bold text-gray-900">Sample DeactivateRecurringBilling Request</h4>
          <DownloadButtons />
        </div>
        <CodeBlock code={DRB_XML} lang="xml" testid="drb-xml" />
      </div>

      {/* Fields */}
      <div id="drb-fields" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Available Form Fields</h3>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#1F4E79] text-white">
                <th className="text-left p-3 w-20">Field</th>
                <th className="text-left p-3">Description</th>
                <th className="text-left p-3 w-24">Required</th>
                <th className="text-left p-3 w-28">Definition</th>
              </tr>
            </thead>
            <tbody>
              {DRB_FIELDS.map((f, i) => (
                <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                  <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                  <td className="p-3">{f.desc}</td>
                  <td className="p-3"><ReqBadge value={f.req} /></td>
                  <td className="p-3 font-mono text-xs">{f.def}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-3">Validation</h3>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#1F4E79] text-white">
                <th className="text-left p-3 w-20">Field</th>
                <th className="text-left p-3">Description</th>
                <th className="text-left p-3">Validation (Regex)</th>
              </tr>
            </thead>
            <tbody>
              {DRB_VALIDATION.map((v, i) => (
                <tr key={v.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                  <td className="p-3 font-mono font-semibold text-[#1F4E79]">{v.name}</td>
                  <td className="p-3">{v.desc}</td>
                  <td className="p-3 font-mono text-xs break-all">{v.regex}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-3">Expected XML Responses</h3>
        <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-700">Both activation and deactivation return <strong className="text-[#c32222]">0043</strong>. Use the <code className="bg-white px-1 py-0.5 rounded font-mono">&lt;R2&gt;</code> text to distinguish which action was performed.</p>
        </div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Sample — Activate RBID</h4>
        <CodeBlock code={DRB_RESP_ACTIVATE} lang="xml" testid="drb-resp-activate" />
        <h4 className="text-sm font-semibold text-gray-900 mb-2 mt-4">Sample — Deactivate RBID</h4>
        <CodeBlock code={DRB_RESP_DEACTIVATE} lang="xml" testid="drb-resp-deactivate" />
      </div>

      <div id="drb-request-reference" className="scroll-mt-28 mt-10">
        <ApiPlayground
          apiSignature="DeactivateRecurringBilling"
          title="Request Reference — Activate / Deactivate RBID"
          description="Toggle an existing RBID's active state."
          defaultData={DRB_XML.replace('<!-- Sample of RBID Activation -->\n', '')}
        />
      </div>
    </section>

    {/* Process RBID */}
    <section id="process-rbid" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Process RBID</h2>

      <div id="prcb-request" className="scroll-mt-28">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Making The Request</h3>
        <p className="text-sm text-gray-700 mb-3">The request string for the <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono">ProcessRecurringBilling</code> call:</p>
        <ol className="list-decimal pl-6 space-y-1 text-gray-700 text-sm mb-4">
          <li><strong>Username</strong> = SomeName</li>
          <li><strong>Password</strong> = SomePassword</li>
          <li><strong>MessageID</strong> = <em>*GUID</em></li>
          <li><strong>APISignature</strong> = ProcessRecurringBilling</li>
          <li><strong>Data</strong> = Form data in XML format</li>
        </ol>
        <CodeBlock code={PRCB_REQUEST_URL} lang="http" testid="prcb-request-url" />

        <div className="flex flex-wrap items-center justify-between gap-3 mb-2 mt-6">
          <h4 className="text-base font-bold text-gray-900">Sample ProcessRecurringBilling Request</h4>
          <DownloadButtons />
        </div>
        <CodeBlock code={PRCB_XML} lang="xml" testid="prcb-xml" />
      </div>

      <div id="prcb-fields" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Available Form Fields</h3>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#1F4E79] text-white">
                <th className="text-left p-3 w-20">Field</th>
                <th className="text-left p-3">Description</th>
                <th className="text-left p-3 w-24">Required</th>
                <th className="text-left p-3 w-28">Definition</th>
              </tr>
            </thead>
            <tbody>
              {PRCB_FIELDS.map((f, i) => (
                <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                  <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                  <td className="p-3">{f.desc}</td>
                  <td className="p-3"><ReqBadge value={f.req} /></td>
                  <td className="p-3 font-mono text-xs">{f.def}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-3">Validation</h3>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#1F4E79] text-white">
                <th className="text-left p-3 w-20">Field</th>
                <th className="text-left p-3">Description</th>
                <th className="text-left p-3">Validation (Regex)</th>
              </tr>
            </thead>
            <tbody>
              {PRCB_VALIDATION.map((v, i) => (
                <tr key={v.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                  <td className="p-3 font-mono font-semibold text-[#1F4E79]">{v.name}</td>
                  <td className="p-3">{v.desc}</td>
                  <td className="p-3 font-mono text-xs break-all">{v.regex}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-3">Transaction Result Fields</h3>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#1F4E79] text-white">
                <th className="text-left p-3 w-20">Field</th>
                <th className="text-left p-3">Description</th>
                <th className="text-left p-3 w-28">Definition</th>
              </tr>
            </thead>
            <tbody>
              {PRCB_RESULT_FIELDS.map((f, i) => (
                <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                  <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                  <td className="p-3">{f.desc}</td>
                  <td className="p-3 font-mono text-xs">{f.def}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-3">Expected XML Responses</h3>
        <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-700">
            <strong className="text-[#c32222]">0051</strong> — Process Recurring Billing Received. Confirmation notification with the overall transaction result will be sent to the Notification URL.
          </p>
        </div>

        <h4 className="text-sm font-semibold text-gray-900 mb-2">Sample XML Response (0051)</h4>
        <CodeBlock code={PRCB_RESP_0051} lang="xml" testid="prcb-resp-0051" />

        <h4 className="text-sm font-semibold text-gray-900 mb-2 mt-4">Sample — Transaction Result (sent to Notification URL)</h4>
        <CodeBlock code={PRCB_RESP_RESULT} lang="xml" testid="prcb-resp-result" />
      </div>

      <div id="prcb-request-reference" className="scroll-mt-28 mt-10">
        <ApiPlayground
          apiSignature="ProcessRecurringBilling"
          title="Request Reference — Process RBID"
          description="Charge a stored RBID without re-collecting card details."
          defaultData={PRCB_XML.replace('<!-- Process Recurring Billing Request Demonstration -->\n', '')}
        />
      </div>
    </section>
  </ContainerPageLayout>
);

export default RecurringBilling;
